import { format } from 'date-fns';
// @ts-ignore - html2pdf.js doesn't have TypeScript definitions
import html2pdf from 'html2pdf.js';

export interface PDFOptions {
  element: HTMLElement;
  filename?: string;
  carName?: string;
}

/**
 * Generates a PDF from an HTML element using html2pdf.js
 * @param options - Configuration options for PDF generation
 * @returns Promise that resolves when PDF is generated
 */
export async function generatePDF(options: PDFOptions): Promise<void> {
  const { element, filename, carName } = options;

  if (!element) {
    throw new Error('Element is required for PDF generation');
  }

  // Wait for all images to load
  await waitForImages(element);

  // Generate filename if not provided
  const pdfFilename = filename || generateFilename(carName);

  // Configure html2pdf options
  const opt = {
    margin: [10, 10, 10, 10],
    filename: pdfFilename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: false,
      letterRendering: true,
      backgroundColor: '#FFFFFF', // White background for PDF
    },
    jsPDF: {
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait' as const,
    },
  };

  try {
    await html2pdf().set(opt).from(element).save();
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
}

/**
 * Waits for all images within an element to load
 */
function waitForImages(element: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    const images = element.querySelectorAll('img');
    const imagePromises: Promise<void>[] = [];

    images.forEach((img) => {
      if (img.complete) {
        return; // Image already loaded
      }

      const promise = new Promise<void>((imgResolve, imgReject) => {
        const timeout = setTimeout(() => {
          console.warn('Image load timeout:', img.src);
          imgResolve(); // Resolve anyway to not block PDF generation
        }, 5000); // 5 second timeout per image

        img.onload = () => {
          clearTimeout(timeout);
          imgResolve();
        };

        img.onerror = () => {
          clearTimeout(timeout);
          console.warn('Image load error:', img.src);
          imgResolve(); // Resolve anyway to not block PDF generation
        };
      });

      imagePromises.push(promise);
    });

    if (imagePromises.length === 0) {
      resolve();
      return;
    }

    Promise.all(imagePromises).then(() => {
      // Small delay to ensure rendering is complete
      setTimeout(resolve, 100);
    });
  });
}

/**
 * Generates a filename for the PDF
 */
function generateFilename(carName?: string): string {
  const date = format(new Date(), 'yyyy-MM-dd');
  const carNamePart = carName
    ? carName.replace(/[^a-z0-9]/gi, '_').toLowerCase()
    : 'car';
  return `HealthReport_${carNamePart}_${date}.pdf`;
}
